// src/map/plateOverlay/routeOverlay.ts
//
// Render the active flight-plan route on the plate overlay.
//
// Lazy: only rendered when `showRouteOnPlate` / `plateOverlayShowOwnship`
// is true. Uses the same magenta dashed style as holds/pts for visual
// consistency with PlanProceduresLayer.

import maplibregl from 'maplibre-gl';
import { getMapInstance } from '../MapView';
import { usePlanStore } from '../../stores/planStore';
import { densifyRouteForGreatCircle } from '../../lib/geo';

// ── Constants ────────────────────────────────────────────────────────────────

export const ROUTE_SOURCE_ID = 'plate-overlay-route';
export const ROUTE_LINE_LAYER_ID = 'plate-overlay-route-line';

// ── Route rendering ──────────────────────────────────────────────────────────

/**
 * Build a GeoJSON FeatureCollection for the active flight plan route,
 * densified for great-circle rendering.
 */
function buildRouteFeatureCollection(): GeoJSON.FeatureCollection {
  const { route } = usePlanStore.getState();
  if (route.length < 2) return { type: 'FeatureCollection', features: [] };
  const coords: Array<[number, number]> = [];
  for (let i = 0; i < route.length - 1; i++) {
    const a = route[i];
    const b = route[i + 1];
    const seg = densifyRouteForGreatCircle([a, b], 25);
    if (i === 0) coords.push(...seg);
    else coords.push(...seg.slice(1)); // avoid duplicate junction point
  }
  if (coords.length < 2) return { type: 'FeatureCollection', features: [] };
  return {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: coords },
      properties: {},
    }],
  };
}

export function addPlateRouteToMap(): void {
  const map = getMapInstance();
  if (!map) return;
  const fc = buildRouteFeatureCollection();
  if (map.getSource(ROUTE_SOURCE_ID)) {
    (map.getSource(ROUTE_SOURCE_ID) as maplibregl.GeoJSONSource).setData(fc);
  } else {
    map.addSource(ROUTE_SOURCE_ID, { type: 'geojson', data: fc });
    map.addLayer({
      id: ROUTE_LINE_LAYER_ID,
      type: 'line',
      source: ROUTE_SOURCE_ID,
      paint: {
        'line-color': '#ec4899',   // magenta, matches holds/pts
        'line-width': 2.5,
        'line-dasharray': [4, 3],
        'line-opacity': 0.9,
      },
    });
  }
}

export function removePlateRouteFromMap(): void {
  const map = getMapInstance();
  if (!map) return;
  if (map.getLayer(ROUTE_LINE_LAYER_ID)) map.removeLayer(ROUTE_LINE_LAYER_ID);
  if (map.getSource(ROUTE_SOURCE_ID)) map.removeSource(ROUTE_SOURCE_ID);
}
